import {filter, takeUntil, tap} from 'rxjs/operators';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Contact, ContactCustomField, ContactTag, ICustomContactFieldOption, IContact, IContactCustomField, IContactTag,
    ICustomContactFieldUser, ITag, Tag, IContactUnsubscribe } from '@routes/addressbook/models/addressbook-model';
import { ModalFormHostStateService } from '@shared/components/modal-form-host/modal-form-host-state.service';
import { BooleanTranslations, CalendarLocales, ContactTypes, Genders, AllLanguages, DefaultLanguage, DefaultLocale, Countries,
    SetStringTimeValueBasedOnTimePickerValue, SetDateBasedOnTimeString,
    SetUtcDateValueBasedOnDatePickerValue } from '@shared/constants/constants';
import { CustomContactFieldType } from '@shared/enums/CustomContactFieldType';
import { FreeTextFormat } from '@shared/enums/FreeTextFormat';
import { IUserForDropdown } from '@shared/services/api/legacy/users-service/user-model';
import * as moment from 'moment';
import * as countries from 'i18n-iso-countries';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';
import { TranslationService } from '@shared/services/translation/translation.service';
import { ICalendarLocales } from '@shared/models/calender-locales-model';
import { IAppState } from '@state/reducers/app/app.reducer';
import { Store } from '@ngrx/store';
import { IState } from '@state/state.module';
import { DatatableComponent } from '@swimlane/ngx-datatable';
import { IPermission, IUnsubscribeSettingsOption } from '@shared/services/api/legacy/settings-service/setting-model';
import { UnsubscribeType } from '@shared/enums/UnsubscribeType';
import { ITicketHolderForDropdown } from '@shared/services/api/legacy/allocations-service/allocations-model';
import { AddNewContact, IAddNewContact } from '@routes/events/models/invitationlist-model';
import { IGroup } from '@shared/services/api/dashboard/groups-service/groups-model';
import { AddContactAddressBook, IAddContact } from '@shared/services/api/dashboard/contacts-service/contacts-model';
import { AddContactInvitation } from '@shared/services/api/dashboard/invitations-service/invitations-model';

@Component({
  selector: 'app-edit-contact-form',
  templateUrl: './edit-contact-form.component.html',
  styleUrls: ['./edit-contact-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class EditContactFormComponent implements OnInit, OnDestroy {
    constructor(
        private state: ModalFormHostStateService,
        private translation: TranslateService,
        private translationService: TranslationService,
        private store: Store<IState>
    ) { }

    public itemsLanguage = AllLanguages(this.translation);
    public itemsContactType = ContactTypes(this.translation);
    public itemsGender = Genders(this.translation);
    public itemsBoolean = BooleanTranslations(this.translation);
    public itemsCountries = [];

    private destroy$ = new ReplaySubject<boolean>();
    private state$: Observable<IAppState>;

    private userId: string;

    public customFieldTypes = CustomContactFieldType;
    public freeTextFormats = FreeTextFormat;
    public customFields: IContactCustomField[];

    public selectedContactTags: ITag[] = [];
    public contactTagsForAutocomplete: ITag[] = [];
    public allContactTags: ITag[] = [];

    public canEdit: boolean;

    // Locale
    public currentLang = DefaultLanguage;
    public calendarLocales: ICalendarLocales = CalendarLocales(DefaultLanguage);
    public locale = DefaultLocale;
    public tenantSettings: IPermission;

    get ticketHolder(): string { return this.ticketHolder$.value; }
    set ticketHolder(value: string) { this.ticketHolder$.next(value); }
    public ticketHolder$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    @Input()
    get callFromAddInvitee(): boolean { return this.callFromAddInvitee$.value; }
    set callFromAddInvitee(value: boolean) { this.callFromAddInvitee$.next(value); }
    public callFromAddInvitee$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    @Input()
    get model(): Contact { return this.model$.value; }
    set model(value: IContact) { this.model$.next(value); }
    public model$: BehaviorSubject<IContact> = new BehaviorSubject<IContact>(new Contact());

    @Input()
    get customFieldsInput(): IContactCustomField[] { return this.customFieldsInput$.value; }
    set customFieldsInput(value: IContactCustomField[]) { this.customFieldsInput$.next(value); }
    public customFieldsInput$: BehaviorSubject<IContactCustomField[]> = new BehaviorSubject<IContactCustomField[]>(undefined);

    @Input()
    get tags(): IContactTag[] { return this.tags$.value; }
    set tags(value: IContactTag[]) { this.tags$.next(value); }
    public tags$: BehaviorSubject<IContactTag[]> = new BehaviorSubject<IContactTag[]>(undefined);

    @Input()
    get usersForDropdown(): IUserForDropdown[] { return this.usersForDropdown$.value; }
    set usersForDropdown(value: IUserForDropdown[]) { this.usersForDropdown$.next(value); }
    public usersForDropdown$: BehaviorSubject<IUserForDropdown[]> = new BehaviorSubject<IUserForDropdown[]>(undefined);

    @Input()
    get unsubscribeOptions(): IUnsubscribeSettingsOption[] { return this.unsubscribeOptions$.value; }
    set unsubscribeOptions(value: IUnsubscribeSettingsOption[]) { this.unsubscribeOptions$.next(value); }
    public unsubscribeOptions$: BehaviorSubject<IUnsubscribeSettingsOption[]> = new BehaviorSubject<IUnsubscribeSettingsOption[]>(undefined);

    @Input()
    get defaultLanguage(): string { return this.defaultLanguage$.value; }
    set defaultLanguage(value: string) { this.defaultLanguage$.next(value); }
    public defaultLanguage$: BehaviorSubject<string> = new BehaviorSubject<string>(null);

    @Input()
    get ticketHoldersForDropdown(): ITicketHolderForDropdown[] { return this.ticketHoldersForDropdown$.value; }
    set ticketHoldersForDropdown(value: ITicketHolderForDropdown[]) { this.ticketHoldersForDropdown$.next(value); }
    public ticketHoldersForDropdown$: BehaviorSubject<ITicketHolderForDropdown[]> = new BehaviorSubject<ITicketHolderForDropdown[]>(undefined);

    @Input()
    get isEdit(): boolean { return this.isEdit$.value; }
    set isEdit(value: boolean) { this.isEdit$.next(value); }
    public isEdit$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    @Input()
    get groupsForDropdown(): IGroup[] { return this.groupsForDropdown$.value; }
    set groupsForDropdown(value: IGroup[]) { this.groupsForDropdown$.next(value); }
    public groupsForDropdown$: BehaviorSubject<IGroup[]> = new BehaviorSubject<IGroup[]>(undefined);

    @Output()
    public formSubmitted = new EventEmitter<IAddContact>();

    @Output()
    public formSubmittedInvitee = new EventEmitter<IAddContact>();

    @ViewChild(NgForm, { static: true })
    set form(value: NgForm) { this.form$.next(value); }
    get form(): NgForm { return this.form$.value; }
    public form$: BehaviorSubject<NgForm> = new BehaviorSubject<NgForm>(undefined);

    @ViewChild(DatatableComponent, { static: true }) public tableLog: DatatableComponent;

    public ngOnInit(): void {
        if (!this.callFromAddInvitee) {
            this.prepareTags();
        }
        this.customFields = this.prepareCustomFields();
        this.state.form = this.form;
        this.form$.pipe(
            takeUntil(this.destroy$),
            filter(x => !!x),)
            .subscribe(x => {
                this.state.form = x;
            });

        this.state$ = this.store.select(x => x.app);
        this.state$.pipe(
            takeUntil(this.destroy$),
            tap(x => {
                this.userId = x.profile.id;
                // Set locale
                this.currentLang = x.profile.languageId;
                this.calendarLocales = CalendarLocales(x.profile.localeId);
                this.locale = x.profile.localeId;
                this.tenantSettings = x.environment.tenantSettings;
            }))
            .subscribe();

        // Set permissions
        if (!this.callFromAddInvitee) {
            this.unsubscribeOptions.forEach(unsubscribeOption => {
                // Default checkbox value is true, which means subscribed
                let tempChecked = true;

                // If contact is edit and unsubscribe id exists, the contact is unsubscribed for this type and the permission is false
                if (this.isEdit) {
                    for (const unsubscribe of this.model?.unsubscribes) {
                        if (unsubscribe?.unsubscribeOptionId === unsubscribeOption?.id) {
                            tempChecked = false;
                            break;
                        }
                    }
                }

                // Set checkbox value
                unsubscribeOption.isChecked = tempChecked;
            });
        }

        if (this.ticketHoldersForDropdown) {
            if (this.ticketHoldersForDropdown.find(x => x.id === this.userId)) {
                this.ticketHolder = this.ticketHoldersForDropdown.find(x => x.id === this.userId).id;
            } else {
                this.ticketHolder = this.ticketHoldersForDropdown[0].id;
            }
        }

        // Set countries dropdown
        this.itemsCountries = Countries(countries, this.currentLang);

        this.canEdit = this.model.canEdit;

        this.groupsForDropdown = this.groupsForDropdown.filter(group => {
            return !group.isDefault && !group.isPublicRegistration && !group.isDynamic;
        });
    }

    public ngOnDestroy(): void {
        this.state.clear();

        this.destroy$.next(true);
        this.destroy$.complete();
    }

    public onSubmit() {
        this.prepareDataForSubmit(this.callFromAddInvitee);

        if(this.callFromAddInvitee){
            this.EmitAddContactInvitee();
        }
        else{
            this.EmitAddContactAddressBook();
        }
    }

    private EmitAddContactInvitee() {
        const newModel: IAddContact = new AddContactInvitation(
            this.ticketHolder === 'unallocated' ? null : this.ticketHolder,
            this.model.firstName,
            this.model.lastNamePrefix,
            this.model.lastName,
            this.model.title,
            this.model.type,
            this.model.genderId,
            this.model.languageId,
            this.model.email,
            this.model.phone,
            this.model.mobile,
            this.model.companyName,
            this.model.salutation,
            this.model.mailPermission,
            this.model.smsPermission,
            this.model.accountOwnerId,
            this.model.addressLineOne,
            this.model.addressLineTwo,
            this.model.city,
            this.model.postalCode,
            this.model.country,
            this.model.crmId,
            this.model.customerNumber,
            this.model.groupIds,
            this.model.contactCustomFields
        );

        this.formSubmittedInvitee.emit(newModel);
    }

    private EmitAddContactAddressBook(){
        const newModel: IAddContact = new AddContactAddressBook(
            this.model.id,
            this.ticketHolder === 'unallocated' ? null : this.ticketHolder,
            this.model.firstName,
            this.model.lastNamePrefix,
            this.model.lastName,
            this.model.title,
            this.model.type,
            this.model.genderId,
            this.model.languageId,
            this.model.email,
            this.model.phone,
            this.model.mobile,
            this.model.companyName,
            this.model.salutation,
            this.model.mailPermission,
            this.model.smsPermission,
            this.model.accountOwnerId,
            this.model.addressLineOne,
            this.model.addressLineTwo,
            this.model.city,
            this.model.postalCode,
            this.model.country,
            this.model.crmId,
            this.model.customerNumber,
            this.model.groupIds,
            this.model.contactCustomFields,
            this.model.contactTags.map(x => {return {...x, index: null};}),
            this.model.unsubscribes,
            this.model.logs,
            this.model.companyId,
            this.model.accessCode,
            this.model.canEdit
        );

        this.formSubmitted.emit(newModel);
    }

    private prepareDataForSubmit(callFromAddInvitee: boolean) {
        // Set custom fields
        const customFields = JSON.parse(JSON.stringify(this.customFields));
        customFields.map(field => {
            if (field.contactCustomFieldOption && field.contactCustomFieldOption.id === null) {
                field.contactCustomFieldOption = null;
            }
            if (field.freeTextFormat === FreeTextFormat.Date) {
                field.dateTimeValue = SetUtcDateValueBasedOnDatePickerValue(field.dateTimeValue);
            }
            if (field.freeTextFormat === FreeTextFormat.Time) {
                const time = new Date(field.textValue);
                field.textValue = SetStringTimeValueBasedOnTimePickerValue(time.toString());
            }
            return field;
        });
        this.model.contactCustomFields = customFields.filter(el =>  {
            return el.booleanValue || el.textValue || el.numberValue || el.numberValue === 0 || el.dateTimeValue || el.contactCustomFieldOptions.length > 0 || el.contactCustomFieldUsers.length > 0;
        });

        if(!callFromAddInvitee){
            // Set tags
            const tagsForSubmit: IContactTag[] = [];
            this.selectedContactTags.forEach(tag => {
                tagsForSubmit.push(new ContactTag(tag.value, []));
            });
            this.model.contactTags = tagsForSubmit;

            // Set permissions
            const contactUnsubscribeOptionIds: IContactUnsubscribe[] = [];
            this.unsubscribeOptions.forEach(unsubscribeOption => {
                // Add to unsubscribes if the permission is campaign and unchecked
                if (unsubscribeOption?.unsubscribeOptionType === UnsubscribeType.Campaign && !unsubscribeOption?.isChecked) {
                    contactUnsubscribeOptionIds.push({unsubscribeDate: null, unsubscribeOptionId: unsubscribeOption?.id});
                }
            });
            this.model.unsubscribes = contactUnsubscribeOptionIds;
        }
    }

    public prepareCustomFields(): IContactCustomField[] {
        const customFields = [];
        this.customFieldsInput.forEach(element => {
            customFields.push(new ContactCustomField(
                element.id,
                element.index,
                element.customFieldType,
                element.freeTextFormat,
                element.translations,
                element.contactCustomFieldOptions,
                [],
                [],
                false,
                null,
                null,
                null
            ));
        });
        this.setCustomFieldsData(customFields);
        return customFields;

    }

    private setCustomFieldsData(customFields) {
        if (this.model.contactCustomFields && this.model.contactCustomFields.length) {
            customFields.map(field => {
                const existingField = this.model.contactCustomFields.find(el => el.id === field.id);
                if (existingField) {
                    switch (existingField.freeTextFormat) {
                        case (FreeTextFormat.Text): {
                            field.textValue = existingField.textValue;
                            break;
                        }
                        case (FreeTextFormat.Number): {
                            field.numberValue = existingField.numberValue;
                            break;
                        }
                        case (FreeTextFormat.Date): {
                            existingField.dateTimeValue ? field.dateTimeValue = new Date(moment.utc(existingField.dateTimeValue).local().format()) : field.dateTimeValue = null;
                            break;
                        }
                        case (FreeTextFormat.Time): {
                            field.textValue = SetDateBasedOnTimeString(existingField.textValue);
                            break;
                        }case (FreeTextFormat.CountryCode): {
                            field.textValue = existingField.textValue;
                            break;
                        }
                    }
                    field.booleanValue = existingField.booleanValue;
                    field.contactCustomFieldOptions = existingField.contactCustomFieldOptions;
                    field.contactCustomFieldUsers = existingField.contactCustomFieldUsers;

                    if (existingField.customFieldType === CustomContactFieldType.MultipleSelect) {
                        field.selectedMultiOptions = [];
                        existingField.contactCustomFieldOptions.forEach(option => {
                            field.selectedMultiOptions.push({
                                id: option.id,
                                text: this.translationService.getTranslationValue(option.translations, this.currentLang, this.defaultLanguage)
                            });
                        });
                    }

                    if (existingField.customFieldType === CustomContactFieldType.MultiSelectUser) {
                        field.selectedMultiUser = [];
                        existingField.contactCustomFieldUsers.forEach(user => {
                            const userToAdd = this.usersForDropdown.find(el => el.id === user.userId);
                            if (userToAdd) {
                                field.selectedMultiUser.push(userToAdd);
                            }
                        });
                    }

                    return field;
                }
            });
            return customFields;
        }
    }

    public getCustomFieldName(field) {
        return this.translationService.getTranslationValue(field.translations, this.currentLang, this.defaultLanguage);
    }

    public onSelectCustomOption(event, field) {
        const option = field.availableCustomFieldOptions.find(el => el.id === event);
        if (option) {
            const contactCustomFieldOptions: ICustomContactFieldOption[] = [{ id: option.id, index: option.index, translations: option.translations }];
            field.contactCustomFieldOptions = contactCustomFieldOptions;
        }
    }

    public onRemoveCustomOption(field) {
        const contactCustomFieldOptions: ICustomContactFieldOption[] = [];
        field.contactCustomFieldOptions = contactCustomFieldOptions;
    }

    public onSelectMultiOptions(event, field) {
        const contactCustomFieldOptions: ICustomContactFieldOption[] = [];
        event.value.forEach(value => contactCustomFieldOptions.push({ id: value.id, index: value.index, translations: value.translations }));
        field.contactCustomFieldOptions = contactCustomFieldOptions;
    }

    public onSelectSingleUser(event, field) {
        const selectedUser = this.usersForDropdown.find(el => el.id === event);
        if (selectedUser) {
            const contactCustomFieldUsers: ICustomContactFieldUser[] = [{ userId: selectedUser.id }];
            field.contactCustomFieldUsers = contactCustomFieldUsers;
        }
     }

    public onRemoveSingleUser(field) {
        const contactCustomFieldUsers: ICustomContactFieldUser[] = [];
        field.contactCustomFieldUsers = contactCustomFieldUsers;
    }

    public onSelectMultiUser(event, field) {
        const contactCustomFieldUsers: ICustomContactFieldUser[] = [];
        event.value.forEach(value => contactCustomFieldUsers.push({ userId: value.id }));
        field.contactCustomFieldUsers = contactCustomFieldUsers;
    }

    private prepareTags() {
        this.model.contactTags.forEach(tag => {
            this.selectedContactTags.push(new Tag(tag.id, this.getCustomFieldName(tag)));
        });
        this.tags.forEach(tag => {
            this.allContactTags.push(new Tag(tag.id, this.getCustomFieldName(tag)));
        });
        this.filterTags();
    }

    public removeTag(tag) {
        this.selectedContactTags = this.selectedContactTags.filter(el => el.value !== tag.value);
        this.filterTags();
    }

    public getTagsForAutocomplate() {
        if (this.selectedContactTags.length) {
            return  this.allContactTags.filter(tag => {
                return this.selectedContactTags.findIndex(el => el.value === tag.value) === -1;
            });
        } else {
            return this.contactTagsForAutocomplete = this.allContactTags;
        }
    }

    private filterTags() {
        this.contactTagsForAutocomplete =  this.getTagsForAutocomplate();
    }

}
